import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Viewport from './Viewport';
import { getMeetingCapability } from '@/common/http/meet';
import { isLicenseExpire, isSuccessCode } from '@/common/utils/response';
import { setCapability } from '@/common/store/whole/slice';
import { TStore } from '@/common/basis/types/tstore';
import { userCurrentInfo } from '@/common/http/user';
import { TField } from '@/common/basis/types/tfield';
import { setUser } from '@/common/store/person/slice';
import { TResponse } from '@/common/basis/types/tresponse';
import ModalErrorExit from '@/components/HOC/ModalErrorExit';
import { usePersonLogin } from '@/common/hooks/person-login';
import { TRouter } from '@/common/basis/types/trouter';
import { getCookieUserToken, removeCookieUserToken } from '@/common/utils/cookies';

function App() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const token = getCookieUserToken();
  const navigate = useNavigate();
  const { isLogined } = usePersonLogin();
  const user = useSelector((state: TStore.State) => state.person.user);
  const isLicenceTips = useRef(false); // 是否显示授权到期提示
  const [messageApi, contextHolder] = message.useMessage({ top: 60 });
  const [exitErrorMsg, setExitErrorMsg] = useState('');
  const [isOpenErrorExit, setIsOpenErrorExit] = useState(false);

  // 获取全局配置信息
  const requestMeetingCapability = async () => {
    const resp = await getMeetingCapability();

    if (isSuccessCode(resp?.code)) {
      dispatch(setCapability(resp.data));
    }
  };

  // 获取用户信息
  const handUserInfo = async (isStup = false) => {
    // 用户未登录直接返回
    if (!token) {
      return;
    }
    try {
      const resp = await userCurrentInfo();

      if (isSuccessCode(resp?.code)) {
        const data = resp?.data;
        const licenceExpire = data?.licenceExpire ?? 0;
        const isLicenceTrail = data?.licenceTrail ?? true; // 是否是免费试用版
        const roleCode = data?.roleCode ?? TField.USER_POWSER_NORMAL;
        const isAdminUser = roleCode === TField.USER_POWSER_ADMIN; // 是否是管理员用户
        const days = dayjs(licenceExpire).diff(new Date().getTime(), 'day');

        // 兼容会议用户等字段信息
        dispatch(setUser({ ...user, userName: data?.name, ...data, token }));

        if (days >= 0 && days <= 7) {
          if (isLicenceTips.current) {
            return;
          }
          isLicenceTips.current = true;
          messageApi.destroy();

          // 快到期时,仅管理员用户显示提示
          if (isAdminUser) {
            if (days === 0) {
              if (isLicenceTrail) {
                messageApi.warning(t('trialReminderLessOne'));
              } else {
                messageApi.warning(t('licenseReminderLessOne'));
              }
            } else {
              if (isLicenceTrail) {
                messageApi.warning(t('trialReminder', { days })); // 免费试用版免费试用版还剩{{days}}天，请及时购买正版
              } else {
                messageApi.warning(t('licenseReminder', { days })); // 产品授权期限还剩{{days}}天，请及时续费购买
              }
            }
            await new Promise((resolve) => setTimeout(() => resolve(true), 3000));
          }
        } else {
          isLicenceTips.current = false;
        }
      } else if (isLicenseExpire(resp?.code)) {
        const isLicenceTrail = resp?.data?.licenceTrail ?? true;
        const roleCode = resp?.data?.roleCode ?? '';
        const isAdminUser = roleCode === TField.USER_POWSER_ADMIN;

        if (isLicenseExpire(resp?.code)) {
          messageApi.destroy();
          if (isLicenceTrail) {
            if (isAdminUser) {
              messageApi.warning(t('trialExpiredAdmin')); // 免费试用已到期(管理员登录提示)
            } else {
              messageApi.warning(t('trialExpired')); // 免费试用已到期(非管理员登录提示)
            }
          } else {
            if (isAdminUser) {
              messageApi.warning(t('licenseExpiredAdmin')); // 正式版已到期(管理员登录提示)
            } else {
              messageApi.warning(t('licenseExpired')); // 正式版已到期(非管理员登录提示)
            }
          }
        }
        await new Promise((resolve) => setTimeout(() => resolve(true), 3000));
        removeCookieUserToken();

        // await window.electron?.browserWindow.setOpacity(0, TRouter.ROUTE_ROOT);
        // await new Promise((resolve) => setTimeout(() => resolve(true), 200));
        // navigate(TRouter.ROUTE_HOME, { replace: true });
        // closeOtherWindows();
        // await manageToHomeLoginScreen();
        // await new Promise((resolve) => setTimeout(() => resolve(true), 200));
        // await window.electron?.browserWindow.setOpacity(1, TRouter.ROUTE_ROOT);
      } else if (resp?.code === TResponse.CODE_401) {
        setExitErrorMsg(resp.msg); // 登录在其他设备或者用户被禁用 返回服务端提示
      }
    } catch (err) {
      // 启动应用时，如果网络异常，则打开错误弹窗
      if (isStup) {
        setExitErrorMsg(t('appErrorTip'));
      }
    }
  };

  useEffect(() => {
    if (!isOpenErrorExit) {
      setExitErrorMsg('');
    }
  }, [isOpenErrorExit]);

  useEffect(() => {
    if (exitErrorMsg) {
      setIsOpenErrorExit(true);
    }
  }, [exitErrorMsg]);

  useEffect(() => {
    if (isLogined) {
      Promise.all([handUserInfo(true), requestMeetingCapability()]);
    }
  }, [isLogined]);

  // 判断登录状态若登录设置用户token, 若无token直接跳转至登录页
  useEffect(() => {
    if (token) {
      dispatch(setUser({ ...user, token }));
    } else {
      navigate(TRouter.ROUTE_LOGIN);
    }
  }, []);

  return isLogined ? (
    <>
      <Viewport />
      <ModalErrorExit isOpen={isOpenErrorExit} setIsOpen={setIsOpenErrorExit}>
        <div className='py-6'>
          <span>{exitErrorMsg}</span>
        </div>
      </ModalErrorExit>
      {contextHolder}
    </>
  ) : (
    <div className='flex h-screen w-screen items-center justify-center'>
      <Spin />
    </div>
  );
}

export default App;
